import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import ReactDOM from "react-dom";
import api from "../utils/api";
import { showGlobalToast } from "../context/ToastContext";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import {
  UserPlus,
  Trash2,
  Shield,
  Search,
  Edit2,
  X,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Bot,
  ShieldCheck
} from "lucide-react";

// Minimalistic Custom Select with Body Portal & Smart Viewport Positioning
const CustomSelect = ({ value, options, onChange, label, className = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0, openUpward: false });

  const updatePos = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUpward = spaceBelow < 220 && spaceAbove > 180;
      setPos({
        top: openUpward ? rect.top - 6 : rect.bottom + 6,
        left: rect.left,
        width: rect.width,
        openUpward
      });
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      updatePos();
      window.addEventListener("scroll", updatePos, true);
      window.addEventListener("resize", updatePos);
    }
    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [isOpen, updatePos]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => opt.value === value);

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {label && (
        <label className="block text-[11px] font-semibold text-zinc-700 uppercase tracking-wider mb-1.5">
          {label}
        </label>
      )}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-xs font-medium text-zinc-900 hover:bg-zinc-100/70 focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 transition-all cursor-pointer"
      >
        <span className="truncate">{selectedOption?.label || "Select Option"}</span>
        <ChevronDown
          size={14}
          className={`text-zinc-400 transition-transform duration-200 ${isOpen ? "rotate-180 text-zinc-800" : ""}`}
        />
      </button>

      {isOpen &&
        ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: "fixed",
              top: `${pos.top}px`,
              left: `${pos.left}px`,
              width: `${pos.width}px`,
              transform: pos.openUpward ? "translateY(-100%)" : "none",
              zIndex: 99999
            }}
            className="bg-white border border-zinc-200 rounded-xl shadow-2xl overflow-hidden animate-fadeIn"
          >
            <div className="max-h-52 overflow-y-auto custom-scrollbar p-1 space-y-0.5">
              {options.map((opt) => (
                <button
                  key={String(opt.value)}
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                  }}
                  className={`w-full px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer flex items-center justify-between text-left ${
                    value === opt.value
                      ? "bg-zinc-900 text-white font-semibold"
                      : "text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900"
                  }`}
                >
                  <span>{opt.label}</span>
                  {value === opt.value && <Check size={13} className="text-white shrink-0 ml-1.5" />}
                </button>
              ))}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

// Minimalistic Multi-select with Body Portal & Smart Viewport Positioning
const MultiSelect = ({ value = [], options, onChange, label, className = "" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [botSearch, setBotSearch] = useState("");
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0, openUpward: false });

  const updatePos = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUpward = spaceBelow < 250 && spaceAbove > 200;
      setPos({
        top: openUpward ? rect.top - 6 : rect.bottom + 6,
        left: rect.left,
        width: rect.width,
        openUpward
      });
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      updatePos();
      window.addEventListener("scroll", updatePos, true);
      window.addEventListener("resize", updatePos);
    }
    return () => {
      window.removeEventListener("scroll", updatePos, true);
      window.removeEventListener("resize", updatePos);
    };
  }, [isOpen, updatePos]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOption = (optionValue) => {
    const newValue = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];
    onChange(newValue);
  };

  const selectedLabels = options
    .filter((opt) => value.includes(opt.value))
    .map((opt) => opt.label);

  const filteredOptions = options.filter((opt) =>
    opt.label.toLowerCase().includes(botSearch.toLowerCase())
  );

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <div className="flex items-center justify-between mb-1.5">
        {label && (
          <label className="text-[11px] font-semibold text-zinc-700 uppercase tracking-wider">
            {label}
          </label>
        )}
        <span className="text-[10px] text-zinc-400 font-medium">
          {value.length} of {options.length} selected
        </span>
      </div>

      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-xs font-medium text-zinc-900 hover:bg-zinc-100/70 focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 transition-all cursor-pointer"
      >
        <span className="text-zinc-700 truncate pr-2">
          {selectedLabels.length > 0 ? selectedLabels.join(", ") : "No Bots Assigned"}
        </span>
        <div className="flex items-center gap-1.5 shrink-0">
          {selectedLabels.length > 0 && (
            <span className="bg-zinc-900 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded">
              {selectedLabels.length}
            </span>
          )}
          <ChevronDown
            size={14}
            className={`text-zinc-400 transition-transform duration-200 ${isOpen ? "rotate-180 text-zinc-800" : ""}`}
          />
        </div>
      </button>

      {isOpen &&
        ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            style={{
              position: "fixed",
              top: `${pos.top}px`,
              left: `${pos.left}px`,
              width: `${pos.width}px`,
              transform: pos.openUpward ? "translateY(-100%)" : "none",
              zIndex: 99999
            }}
            className="bg-white border border-zinc-200 rounded-xl shadow-2xl overflow-hidden animate-fadeIn"
          >
            {options.length > 4 && (
              <div className="p-2 border-b border-zinc-100">
                <input
                  type="text"
                  placeholder="Filter bots..."
                  value={botSearch}
                  onChange={(e) => setBotSearch(e.target.value)}
                  className="w-full px-2.5 py-1.5 text-xs bg-zinc-50 border border-zinc-200 rounded-md focus:outline-none focus:border-zinc-900"
                />
              </div>
            )}
            <div className="max-h-56 overflow-y-auto custom-scrollbar p-1 space-y-0.5">
              {filteredOptions.length === 0 ? (
                <div className="text-[11px] text-zinc-400 p-2 text-center">No matching bots</div>
              ) : (
                filteredOptions.map((opt) => {
                  const isSelected = value.includes(opt.value);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => toggleOption(opt.value)}
                      className={`w-full px-2.5 py-1.5 text-xs rounded-md transition-colors cursor-pointer flex items-center justify-between text-left ${
                        isSelected ? "bg-zinc-100 text-zinc-900 font-medium" : "text-zinc-600 hover:bg-zinc-50"
                      }`}
                    >
                      <div className="flex items-center gap-2 truncate">
                        <div
                          className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition-all shrink-0 ${
                            isSelected ? "bg-zinc-900 border-zinc-900" : "bg-white border-zinc-300"
                          }`}
                        >
                          {isSelected && <Check size={10} className="text-white" strokeWidth={3} />}
                        </div>
                        <span className="truncate">{opt.label}</span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

const UserManagement = ({ agents: propAgents = [], aiAgents: propAiAgents = [] }) => {
  const [internalAgents, setInternalAgents] = useState([]);

  useEffect(() => {
    const loadAiAgents = async () => {
      try {
        const res = await api.request("/api/ai-agents");
        if (Array.isArray(res)) {
          setInternalAgents(res);
        }
      } catch (err) {
        console.error("Failed to fetch ai agents in UserManagement:", err);
      }
    };
    if ((!propAgents || propAgents.length === 0) && (!propAiAgents || propAiAgents.length === 0)) {
      loadAiAgents();
    }
  }, [propAgents, propAiAgents]);

  const agents = (propAgents && propAgents.length > 0)
    ? propAgents
    : (propAiAgents && propAiAgents.length > 0)
      ? propAiAgents
      : internalAgents;
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const USERS_PER_PAGE = 10;

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    role: "human_agent",
    assigned_ai_agents: []
  });

  const [editFormData, setEditFormData] = useState({
    id: null,
    username: "",
    email: "",
    password: "",
    role: "human_agent",
    assigned_ai_agents: [],
    is_active: true
  });

  const [billingInfo, setBillingInfo] = useState({
    maxUsers: 3,
    planName: "Starter"
  });

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await api.request("/users");
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    const loadBillingInfo = async () => {
      try {
        const userStr = sessionStorage.getItem("user");
        const user = userStr ? JSON.parse(userStr) : null;
        const tenantId = user?.tenant_id || user?.username || "admin";

        const subRes = await api.request(`/api/subscription/${tenantId}`);
        let maxUsers = 0;
        let planName = "No Plan";
        if (subRes && subRes.success && subRes.data) {
          const planId = subRes.data.plan_id.toLowerCase();
          planName = subRes.data.plan_name;
          const PLAN_PRESETS = {
            free_trial: 1,
            starter: 3,
            growth: 5,
            professional: 7,
            enterprise: 999
          };
          maxUsers = (PLAN_PRESETS[planId] || 0) + (subRes.data.extra_human_agents || 0);
        }
        setBillingInfo({ maxUsers, planName });
      } catch (err) {
        console.error("Failed to load billing limits for UserManagement page:", err);
      }
    };

    loadBillingInfo();
  }, []);

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!formData.password || formData.password.length < 8) {
      showGlobalToast("Password must be at least 8 characters long", "error");
      return;
    }
    if (!/[A-Za-z]/.test(formData.password) || !/[0-9]/.test(formData.password)) {
      showGlobalToast("Password must contain both letters and numbers", "error");
      return;
    }
    try {
      await api.request("/users", {
        method: "POST",
        body: JSON.stringify(formData)
      });

      showGlobalToast(`Identity Provisioned Successfully for ${formData.username}`, "success");
      setShowAddModal(false);
      setFormData({
        username: "",
        email: "",
        password: "",
        role: "human_agent",
        assigned_ai_agents: []
      });
      fetchUsers();
    } catch (error) {
      console.error("Creation Failed:", error);
      showGlobalToast(error.message || "Failed to create team member", "error");
    }
  };

  const handleEditClick = (user) => {
    setEditFormData({
      id: user.id,
      username: user.username,
      email: user.email,
      password: "",
      role: user.role,
      assigned_ai_agents: user.assigned_ai_agents || [],
      is_active: user.is_active !== false
    });
    setShowEditModal(true);
  };

  const handleEditUserSubmit = async (e) => {
    e.preventDefault();
    if (editFormData.password) {
      if (editFormData.password.length < 8) {
        showGlobalToast("Password must be at least 8 characters long", "error");
        return;
      }
      if (!/[A-Za-z]/.test(editFormData.password) || !/[0-9]/.test(editFormData.password)) {
        showGlobalToast("Password must contain both letters and numbers", "error");
        return;
      }
    }
    try {
      const updates = {
        username: editFormData.username,
        email: editFormData.email,
        role: editFormData.role,
        assigned_ai_agents: editFormData.assigned_ai_agents,
        is_active: editFormData.is_active
      };

      if (editFormData.password) {
        updates.password = editFormData.password;
      }

      await api.request(`/users/${editFormData.id}`, {
        method: "PATCH",
        body: JSON.stringify(updates)
      });

      showGlobalToast("Identity & Access Protocol Updated Successfully", "success");
      setShowEditModal(false);
      fetchUsers();
    } catch (error) {
      console.error("Update Failed:", error);
      showGlobalToast(error.message || "Failed to update team member", "error");
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      await api.request(`/users/${userId}`, {
        method: "PATCH",
        body: JSON.stringify(updates)
      });
      showGlobalToast("Identity Status Updated", "success");
      fetchUsers();
    } catch (error) {
      console.error("Update Failed:", error);
      showGlobalToast("Failed to update status", "error");
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    setIsDeleting(true);
    try {
      await api.request(`/users/${userToDelete.id}`, {
        method: "DELETE"
      });
      showGlobalToast("Identity Deleted Successfully", "success");
      setUserToDelete(null);
      fetchUsers();
    } catch (error) {
      console.error("Deletion Failed:", error);
      showGlobalToast("Failed to delete identity", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredUsers = useMemo(() => {
    return users
      .filter((user) =>
        user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.role?.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => {
        if (a.role === "super_admin") return -1;
        if (b.role === "super_admin") return 1;
        if (a.role === "admin" && b.role !== "admin") return -1;
        if (a.role !== "admin" && b.role === "admin") return 1;
        return (a.username || "").localeCompare(b.username || "");
      });
  }, [users, searchTerm]);

  const agentOptions = agents.map((p) => ({ value: p.id, label: p.name }));
  const roleOptions = [
    { value: "admin", label: "Admin (Scoped to Assigned Bots)" },
    { value: "human_agent", label: "Human Agent" },
    { value: "user", label: "User" }
  ];


  const totalPages = Math.ceil(filteredUsers.length / USERS_PER_PAGE);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * USERS_PER_PAGE,
    currentPage * USERS_PER_PAGE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif] animate-slideIn">
      <div className="w-full max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 sm:pb-6 border-b border-zinc-100 gap-3 sm:gap-4">
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">Teams & Roles</h1>
            <p className="text-xs text-zinc-500 mt-0.5 sm:mt-1">
              Provision operator identities, configure roles, and orchestrate access control for your system.
            </p>
          </div>
          <button
            onClick={() => {
              if (users.length >= billingInfo.maxUsers) {
                showGlobalToast(
                  `You have reached the maximum limit of ${billingInfo.maxUsers} users allowed on your current plan.`,
                  "error"
                );
                return;
              }
              setShowAddModal(true);
            }}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 px-3.5 py-2.5 sm:py-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 transition-all font-semibold text-xs shadow-xs active:scale-[0.98] cursor-pointer"
          >
            <UserPlus size={14} strokeWidth={2.5} />
            <span>Provision Member</span>
          </button>
        </div>


        {/* Search Bar */}
        <div className="flex items-center justify-between gap-3 py-3">
          <div className="relative flex-1 max-w-md">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              type="text"
              placeholder="Search by username, email, or role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-8 pr-4 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-xs text-zinc-800 placeholder:text-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 focus:bg-white transition-all"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-zinc-400 hover:text-zinc-700 font-medium"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Content Area */}
        <div className="mt-2 bg-white rounded-xl border border-zinc-200 shadow-2xs overflow-hidden">
          {isLoading && users.length === 0 ? (
            <div className="py-20 flex flex-col items-center justify-center text-zinc-400">
              <Loader2 size={24} className="animate-spin mb-2 text-zinc-600" />
              <span className="text-xs font-medium">Loading team members...</span>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-16 px-4">
              <h3 className="text-xs font-semibold text-zinc-900">No Members Found</h3>
              <p className="text-xs text-zinc-400 mt-1 max-w-xs mx-auto">
                Provision team members and define access roles to manage your platform.
              </p>
            </div>
          ) : (
            <>
              {/* Desktop Table View (>= md) */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-zinc-50/80 border-b border-zinc-200 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">
                    <tr>
                      <th className="px-5 py-3">Member</th>
                      <th className="px-5 py-3">Role</th>
                      <th className="px-5 py-3">Assigned AI Agents</th>
                      <th className="px-5 py-3 text-center">Status</th>
                      <th className="px-5 py-3">Created</th>
                      <th className="px-5 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100 text-xs">                    {paginatedUsers.map((user) => {
                      const isActive = user.is_active !== false;
                      const isSuperAdmin = user.role === "super_admin";
                      const isAdmin = user.role === "admin";
                      const isHumanAgent = user.role === "human_agent";

                      return (
                        <tr key={user.id} className="hover:bg-zinc-50/60 transition-colors group">
                          <td className="px-5 py-3.5 align-middle">
                            <div className="flex items-center gap-2.5">
                              <div className="w-7 h-7 rounded-full bg-zinc-100 border border-zinc-200 flex items-center justify-center text-[11px] font-bold text-zinc-700 shrink-0 uppercase">
                                {user.username ? user.username.charAt(0) : "U"}
                              </div>
                              <div>
                                <span className="font-semibold text-zinc-900 text-xs block">{user.username}</span>
                                <span className="text-[11px] text-zinc-400 font-normal">{user.email}</span>
                              </div>
                            </div>
                          </td>

                          <td className="px-5 py-3.5 align-middle">
                            {isSuperAdmin ? (
                              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-semibold bg-zinc-900 text-white shadow-2xs">
                                <ShieldCheck size={11} className="text-amber-400" />
                                Super Admin
                              </span>
                            ) : isAdmin ? (
                              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-semibold bg-zinc-800 text-zinc-100 border border-zinc-700 shadow-2xs">
                                <Shield size={11} className="text-zinc-300" />
                                Admin
                              </span>
                            ) : isHumanAgent ? (
                              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-semibold bg-zinc-100 text-zinc-700 border border-zinc-200">
                                <span className="w-1.5 h-1.5 rounded-full bg-zinc-700" />
                                Human Agent
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-semibold bg-zinc-50 text-zinc-500 border border-zinc-200/70">
                                <span className="w-1.5 h-1.5 rounded-full bg-zinc-400" />
                                User
                              </span>
                            )}
                          </td>

                          <td className="px-5 py-3.5 align-middle max-w-xs">
                            <div className="flex flex-wrap gap-1">
                              {isSuperAdmin ? (
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-zinc-600 bg-zinc-100/70 border border-zinc-200/80 px-2 py-0.5 rounded">
                                  <ShieldCheck size={11} className="text-zinc-600" />
                                  Full System Access (ALL)
                                </span>
                              ) : user.assigned_ai_agents && user.assigned_ai_agents.length > 0 ? (
                                user.assigned_ai_agents.map((botId) => {
                                  const bot = agents.find((p) => String(p.id) === String(botId));
                                  return (
                                    <span
                                      key={botId}
                                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-zinc-50 border border-zinc-200 rounded text-[10px] font-medium text-zinc-700"
                                    >
                                      <Bot size={10} className="text-zinc-400" />
                                      {bot?.name || `Bot #${botId}`}
                                    </span>
                                  );
                                })
                              ) : (
                                <span className="text-[11px] text-zinc-400 italic">No Bots Assigned</span>
                              )}
                            </div>
                          </td>

                          <td className="px-5 py-3.5 align-middle text-center">
                            {isSuperAdmin ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/70">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                Active
                              </span>
                            ) : (
                              <button
                                onClick={() => handleUpdateUser(user.id, { is_active: !isActive })}
                                className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold border transition-all cursor-pointer ${
                                  isActive
                                    ? "bg-emerald-50 text-emerald-700 border-emerald-200/70 hover:bg-emerald-100/60"
                                    : "bg-zinc-100 text-zinc-500 border-zinc-200 hover:bg-zinc-200/60"
                                }`}
                                title={isActive ? "Click to suspend account" : "Click to activate account"}
                              >
                                <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-emerald-500" : "bg-zinc-400"}`} />
                                {isActive ? "Active" : "Suspended"}
                              </button>
                            )}
                          </td>

                          <td className="px-5 py-3.5 align-middle text-[11px] text-zinc-400">
                            {(() => {
                              let dateStr = user.created_at;
                              if (dateStr && typeof dateStr === "string") {
                                if (!dateStr.includes("T") && dateStr.includes(" ")) dateStr = dateStr.replace(" ", "T");
                                if (!dateStr.endsWith("Z") && !dateStr.includes("+") && !dateStr.includes("GMT")) dateStr = dateStr + "Z";
                              }
                              const date = new Date(dateStr);
                              return isNaN(date.getTime())
                                ? "—"
                                : date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
                            })()}
                          </td>

                          <td className="px-5 py-3.5 align-middle text-right">
                            <div className="flex items-center justify-end gap-1">
                              {isSuperAdmin ? (
                                <div
                                  className="p-1.5 text-zinc-400 bg-zinc-50 border border-zinc-200/70 rounded-md"
                                  title="Protected Super Admin"
                                >
                                  <ShieldCheck size={13} className="text-amber-500" />
                                </div>
                              ) : (
                                <>
                                  <button
                                    onClick={() => handleEditClick(user)}
                                    className="p-1.5 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 rounded-md transition-colors cursor-pointer"
                                    title="Edit Member"
                                  >
                                    <Edit2 size={13} />
                                  </button>
                                  <button
                                    onClick={() => setUserToDelete(user)}
                                    className="p-1.5 text-zinc-500 hover:text-rose-600 hover:bg-rose-50 rounded-md transition-colors cursor-pointer"
                                    title="Delete Member"
                                  >
                                    <Trash2 size={13} />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card List (< md) */}
              <div className="md:hidden divide-y divide-zinc-100">
                {paginatedUsers.map((user) => {
                  const isActive = user.is_active !== false;
                  const isSuperAdmin = user.role === "super_admin";
                  const isAdmin = user.role === "admin";
                  const isHumanAgent = user.role === "human_agent";

                  return (
                    <div key={user.id} className="p-4 space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-zinc-100 border border-zinc-200 flex items-center justify-center text-xs font-bold text-zinc-700 shrink-0 uppercase">
                            {user.username ? user.username.charAt(0) : "U"}
                          </div>
                          <div className="min-w-0">
                            <span className="font-semibold text-zinc-900 text-xs block truncate">{user.username}</span>
                            <span className="text-[11px] text-zinc-400 truncate block">{user.email}</span>
                          </div>
                        </div>

                        <div className="shrink-0">
                          {isSuperAdmin ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-zinc-900 text-white shadow-2xs">
                              <ShieldCheck size={10} className="text-amber-400" />
                              Super Admin
                            </span>
                          ) : isAdmin ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-zinc-800 text-zinc-100 border border-zinc-700 shadow-2xs">
                              <Shield size={10} className="text-zinc-300" />
                              Admin
                            </span>
                          ) : (
                            <button
                              onClick={() => handleUpdateUser(user.id, { is_active: !isActive })}
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold border transition-all cursor-pointer ${
                                isActive
                                  ? "bg-emerald-50 text-emerald-700 border-emerald-200/70 active:bg-emerald-100"
                                  : "bg-zinc-100 text-zinc-500 border-zinc-200 active:bg-zinc-200"
                              }`}
                            >
                              <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-emerald-500" : "bg-zinc-400"}`} />
                              {isActive ? "Active" : "Suspended"}
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="space-y-1.5 bg-zinc-50/50 p-2.5 rounded-lg border border-zinc-100">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Role</span>
                          <span className="text-zinc-700 font-medium capitalize text-[11px]">
                            {isSuperAdmin ? "Super Admin" : isHumanAgent ? "Human Agent" : isAdmin ? "Admin" : user.role}
                          </span>
                        </div>

                        <div className="pt-1">
                          <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider block mb-1">
                            Bots Access
                          </span>
                          <div className="flex flex-wrap gap-1">
                            {isSuperAdmin ? (
                              <span className="text-[10px] font-semibold text-zinc-600 bg-white px-2 py-0.5 rounded border border-zinc-200">
                                Full System Access (ALL)
                              </span>
                            ) : user.assigned_ai_agents && user.assigned_ai_agents.length > 0 ? (
                              user.assigned_ai_agents.map((botId) => {
                                const bot = agents.find((p) => String(p.id) === String(botId));
                                return (
                                  <span
                                    key={botId}
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-white border border-zinc-200 rounded text-[10px] font-medium text-zinc-700"
                                  >
                                    <Bot size={10} className="text-zinc-400" />
                                    {bot?.name || `#${botId}`}
                                  </span>
                                );
                              })
                            ) : (
                              <span className="text-[10px] text-zinc-400 italic">No Bots Assigned</span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="pt-2 border-t border-zinc-100 flex items-center justify-between">
                        <span className="text-[10px] text-zinc-400">
                          {(() => {
                            let dateStr = user.created_at;
                            if (dateStr && typeof dateStr === "string") {
                              if (!dateStr.includes("T") && dateStr.includes(" ")) dateStr = dateStr.replace(" ", "T");
                              if (!dateStr.endsWith("Z") && !dateStr.includes("+") && !dateStr.includes("GMT")) dateStr = dateStr + "Z";
                            }
                            const date = new Date(dateStr);
                            return isNaN(date.getTime()) ? "—" : date.toLocaleDateString();
                          })()}
                        </span>

                        {!isSuperAdmin && (
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => handleEditClick(user)}
                              className="px-2.5 py-1.5 text-zinc-600 hover:text-zinc-900 border border-zinc-200 bg-white rounded-lg text-xs flex items-center gap-1 cursor-pointer active:scale-95 transition-all shadow-2xs"
                            >
                              <Edit2 size={12} />
                              <span>Edit</span>
                            </button>
                            <button
                              onClick={() => setUserToDelete(user)}
                              className="px-2.5 py-1.5 text-rose-600 hover:text-rose-700 border border-rose-200 bg-rose-50/40 rounded-lg text-xs flex items-center gap-1 cursor-pointer active:scale-95 transition-all shadow-2xs"
                            >
                              <Trash2 size={12} />
                              <span>Delete</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination / Footer */}
              <div className="px-4 py-3 bg-zinc-50/60 border-t border-zinc-100 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] text-zinc-400 font-medium">
                <div>
                  Showing {(currentPage - 1) * USERS_PER_PAGE + 1} to {Math.min(currentPage * USERS_PER_PAGE, filteredUsers.length)} of {filteredUsers.length} members
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="p-1 text-zinc-400 hover:text-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <span className="text-zinc-700 font-semibold text-xs px-2">
                      {currentPage} / {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="p-1 text-zinc-400 hover:text-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Provision Member Modal */}
      {showAddModal &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
            <div
              className="fixed inset-0 bg-zinc-900/50 backdrop-blur-sm transition-opacity animate-fadeIn"
              onClick={() => setShowAddModal(false)}
            />

            <div className="relative bg-white w-full max-w-md rounded-2xl border border-zinc-200 shadow-2xl overflow-hidden animate-scaleIn z-10 my-auto max-h-[90vh] flex flex-col">
              <div className="px-5 py-4 border-b border-zinc-100 flex justify-between items-center bg-zinc-50/60 shrink-0">
                <div>
                  <h3 className="font-semibold text-zinc-900 text-sm">Provision Team Member</h3>
                  <p className="text-[11px] text-zinc-400 mt-0.5">Assign credentials and roles</p>
                </div>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors cursor-pointer"
                >
                  <X size={16} />
                </button>
              </div>

              <form onSubmit={handleAddUser} className="p-4 sm:p-5 space-y-4 overflow-y-auto flex-1 custom-scrollbar">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-zinc-700 uppercase tracking-wider mb-1.5">
                      Username
                    </label>
                    <input
                      required
                      type="text"
                      value={formData.username}
                      onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                      placeholder="e.g. alex_ops"
                      className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 focus:bg-white transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-zinc-700 uppercase tracking-wider mb-1.5">
                      Email
                    </label>
                    <input
                      required
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="alex@company.com"
                      className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 focus:bg-white transition-all"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-zinc-700 uppercase tracking-wider mb-1.5">
                      Password
                    </label>
                    <input
                      required
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Min 8 characters (letters & numbers)" minLength={8}
                      className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 focus:bg-white transition-all"
                    />
                  </div>
                  <CustomSelect
                    label="System Role"
                    value={formData.role}
                    options={roleOptions}
                    onChange={(val) => setFormData({ ...formData, role: val })}
                  />
                </div>

                <div>
                  <MultiSelect
                    label="Assigned Bots (Access Control)"
                    value={formData.assigned_ai_agents}
                    options={agentOptions}
                    onChange={(val) => setFormData({ ...formData, assigned_ai_agents: val })}
                  />
                </div>

                <div className="pt-3 border-t border-zinc-100 flex items-center justify-end gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-3 py-2 border border-zinc-200 text-zinc-600 rounded-lg hover:bg-zinc-50 text-xs font-semibold transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 text-xs font-semibold shadow-xs transition-colors cursor-pointer flex items-center gap-1.5"
                  >
                    <Check size={13} strokeWidth={2.5} />
                    <span>Confirm Member</span>
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {/* Edit Member Modal */}
      {showEditModal &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
            <div
              className="fixed inset-0 bg-zinc-900/50 backdrop-blur-sm transition-opacity animate-fadeIn"
              onClick={() => setShowEditModal(false)}
            />

            <div className="relative bg-white w-full max-w-md rounded-2xl border border-zinc-200 shadow-2xl overflow-hidden animate-scaleIn z-10 my-auto max-h-[90vh] flex flex-col">
              <div className="px-5 py-4 border-b border-zinc-100 flex justify-between items-center bg-zinc-50/60 shrink-0">
                <div>
                  <h3 className="font-semibold text-zinc-900 text-sm">Edit Team Member</h3>
                  <p className="text-[11px] text-zinc-400 mt-0.5">Update credentials and permissions</p>
                </div>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors cursor-pointer"
                >
                  <X size={16} />
                </button>
              </div>

              <form
                onSubmit={handleEditUserSubmit}
                className="p-4 sm:p-5 space-y-4 overflow-y-auto flex-1 custom-scrollbar"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-zinc-700 uppercase tracking-wider mb-1.5">
                      Username
                    </label>
                    <input
                      required
                      type="text"
                      value={editFormData.username}
                      onChange={(e) => setEditFormData({ ...editFormData, username: e.target.value })}
                      className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 focus:bg-white transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-zinc-700 uppercase tracking-wider mb-1.5">
                      Email
                    </label>
                    <input
                      required
                      type="email"
                      value={editFormData.email}
                      onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                      className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 focus:bg-white transition-all"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-zinc-700 uppercase tracking-wider mb-1.5">
                      Password
                    </label>
                    <input
                      type="password"
                      value={editFormData.password}
                      onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })}
                      placeholder="Leave blank to keep"
                      className="w-full px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-900 focus:border-zinc-900 focus:bg-white transition-all"
                    />
                  </div>
                  <CustomSelect
                    label="System Role"
                    value={editFormData.role}
                    options={roleOptions}
                    onChange={(val) => setEditFormData({ ...editFormData, role: val })}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-1">
                    <CustomSelect
                      label="Status"
                      value={editFormData.is_active}
                      options={[
                        { value: true, label: "Active" },
                        { value: false, label: "Suspended" }
                      ]}
                      onChange={(val) => setEditFormData({ ...editFormData, is_active: val })}
                    />
                  </div>
                </div>

                <div>
                  <MultiSelect
                    label="Assigned Bots"
                    value={editFormData.assigned_ai_agents}
                    options={agentOptions}
                    onChange={(val) => setEditFormData({ ...editFormData, assigned_ai_agents: val })}
                  />
                </div>

                <div className="pt-3 border-t border-zinc-100 flex items-center justify-end gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-3 py-2 border border-zinc-200 text-zinc-600 rounded-lg hover:bg-zinc-50 text-xs font-semibold transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 text-xs font-semibold shadow-xs transition-colors cursor-pointer flex items-center gap-1.5"
                  >
                    <Check size={13} strokeWidth={2.5} />
                    <span>Save Changes</span>
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={!!userToDelete}
        onClose={() => setUserToDelete(null)}
        onConfirm={handleDeleteUser}
        title="Delete Identity"
        message={`Are you sure you want to permanently delete the account for '${userToDelete?.username}'? This action will revoke all permissions immediately.`}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default UserManagement;
